import { useState, useEffect } from 'react';

export interface SessionOnboardingStep {
  id: string;
  title: string;
  description: string;
  targetElement?: string;
  position?: 'top' | 'bottom' | 'left' | 'right' | 'above' | 'center';
}

const SESSION_ONBOARDING_STEPS: SessionOnboardingStep[] = [
  {
    id: 'session-welcome',
    title: 'Welcome to Your Training Session',
    description: 'This is your interactive surgical training environment. Here you can watch training videos, leave timestamped comments, and collaborate with other medical professionals.',
    position: 'bottom'
  },
  {
    id: 'video-player',
    title: 'Video Player',
    description: 'Watch surgical procedures with full video controls. You can play, pause, seek, and adjust playback speed. Comments from other users appear as markers on the timeline.',
    targetElement: '.video-player-container',
    position: 'right'
  },
  {
    id: 'layout-toggle',
    title: 'Layout Options',
    description: 'Switch between single video view and side-by-side comparison view to compare multiple videos simultaneously.',
    targetElement: '.layout-toggle',
    position: 'bottom'
  },
  {
    id: 'labeling-modes',
    title: 'Labeling Modes',
    description: 'Choose your labeling approach: "Start/End" mode for precise segment marking, or "Duration" mode for quick time-based annotations. This controls how you create labeled segments on the video timeline.',
    targetElement: '.labeling-mode-selector',
    position: 'left'
  },
  {
    id: 'comment-input',
    title: 'Writing Comments',
    description: 'Type your observations, questions, or insights here. Comments are automatically timestamped to the current video position. Your input helps create a collaborative learning environment.',
    targetElement: '.comment-textarea',
    position: 'left'
  },
  {
    id: 'quick-templates',
    title: 'Quick Templates',
    description: 'Use pre-made comment templates for common observations. Click these buttons to quickly insert structured comments like "Excellent technique" or "Question about procedure". Save time while maintaining consistency.',
    targetElement: '.quick-templates-section',
    position: 'left'
  },
  {
    id: 'start-label',
    title: 'Start Label Button',
    description: 'Click "Start Label" to begin marking a video segment. This creates the beginning point of your labeled section. The button will change to show you\'re actively labeling.',
    targetElement: '.start-label-button',
    position: 'left'
  },
  {
    id: 'end-label',
    title: 'End Label Button',
    description: 'After starting a label, click "End Label" to complete the segment. This creates a colored bar on the video timeline showing your labeled section with the comment attached.',
    targetElement: '.end-label-button',
    position: 'left'
  },
  {
    id: 'video-timeline',
    title: 'Interactive Timeline',
    description: 'The timeline shows your progress and all labeled segments. Colored bars represent different types of comments and labels. Click anywhere on the timeline to jump to that moment in the video.',
    targetElement: '.video-timeline',
    position: 'right'
  },
  {
    id: 'comment-submission',
    title: 'Submit Your Comment',
    description: 'Once you\'ve written your comment and optionally created labels, click this button to save your contribution. Your timestamped comment will appear in the timeline for other learners to see.',
    targetElement: '.submit-comment-button',
    position: 'left'
  },
  {
    id: 'comments-timeline',
    title: 'Comments from Others',
    description: 'View all comments from other users with timestamps. Click on any comment to jump to that moment in the video and see what others have observed. Learn from the collective knowledge of your peers.',
    targetElement: '.comment-list-container',
    position: 'left'
  },
  {
    id: 'video-list',
    title: '🎉 Congratulations! You\'re Ready to Learn!',
    description: 'You\'ve mastered the training platform! This floating video list lets you explore different surgical perspectives and techniques. Click here anytime to switch between videos. Now go ahead and create your first comment or label - we\'re excited to see your contributions to the learning community!',
    position: 'center'
  }
];

const SESSION_ONBOARDING_KEY = 'surgical_training_session_onboarding';
const SESSION_ONBOARDING_PERMANENT_KEY = 'surgical_training_session_onboarding_dont_show_again';

export const useSessionOnboarding = () => {
  const [isOnboardingActive, setIsOnboardingActive] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(false);

  useEffect(() => {
    // Check if user has permanently disabled onboarding
    const dontShowAgain = localStorage.getItem(SESSION_ONBOARDING_PERMANENT_KEY);

    if (dontShowAgain === 'true') {
      setHasCompletedOnboarding(true);
      setIsOnboardingActive(false);
      return;
    }

    // Development mode: Show onboarding on every page reload (but still respect "don't show again")
    const isDevelopment = import.meta.env.DEV;

    if (isDevelopment) {
      // In development, show onboarding unless user clicked "don't show again"
      setIsOnboardingActive(true);
      setHasCompletedOnboarding(false);
    } else {
      // Production mode: Check if user has already seen onboarding in this session
      const completedThisSession = sessionStorage.getItem(SESSION_ONBOARDING_KEY);

      if (completedThisSession === 'true') {
        setHasCompletedOnboarding(true);
      } else {
        // Show onboarding on every login/session
        setIsOnboardingActive(true);
        setHasCompletedOnboarding(false);
      }
    }
  }, []);

  const nextStep = () => {
    if (currentStepIndex < SESSION_ONBOARDING_STEPS.length - 1) {
      setCurrentStepIndex(prev => prev + 1);
    } else {
      completeOnboarding();
    }
  };

  const previousStep = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
    }
  };

  const skipOnboarding = () => {
    completeOnboarding();
  };

  const completeOnboarding = () => {
    setIsOnboardingActive(false);
    setHasCompletedOnboarding(true);
    sessionStorage.setItem(SESSION_ONBOARDING_KEY, 'true');
  };

  const dontShowAgain = () => {
    localStorage.setItem(SESSION_ONBOARDING_PERMANENT_KEY, 'true');
    setIsOnboardingActive(false);
    setHasCompletedOnboarding(true);
  };

  const resetOnboarding = () => {
    sessionStorage.removeItem(SESSION_ONBOARDING_KEY);
    localStorage.removeItem(SESSION_ONBOARDING_PERMANENT_KEY);
    setCurrentStepIndex(0);
    setIsOnboardingActive(true);
    setHasCompletedOnboarding(false);
  };

  const currentStep = SESSION_ONBOARDING_STEPS[currentStepIndex];
  const isLastStep = currentStepIndex === SESSION_ONBOARDING_STEPS.length - 1;

  return {
    isOnboardingActive,
    currentStep,
    currentStepIndex,
    totalSteps: SESSION_ONBOARDING_STEPS.length,
    isLastStep,
    hasCompletedOnboarding,
    nextStep,
    previousStep,
    skipOnboarding,
    completeOnboarding,
    dontShowAgain,
    resetOnboarding
  };
};